// Intensity grid derived from the EDITOR Draft (programEdit.ts) instead
// of a stored Program — the composer's live preview recolours on every
// block commit, mid-edit, so it can't go through buildPayload (which
// rejects incomplete drafts). Tolerant by design: an unparsable value or
// a value-less kind simply contributes no band, it never throws.
//
// Same band semantics as programIntensity.ts (the stored-Program twin):
// hardest set wins the (day, week) cell; %-of-MAX or effort both colour;
// `max` stays reserved for %-of-MAX ≥ 85.

import type { Draft, SlotDraft } from "./programEdit.js";
import { setCountAt } from "./programEditRoundSets.js";
import {
  BAND_RANK,
  bandOf,
  higherBand,
  rpeBand,
  rirBand,
  type IntensityBand,
  type IntensityGrid,
  type WeekCell,
} from "./programIntensity.js";
import { readRound, type ReadSet } from "./programProgression.js";
import { pctOfMax, type MaxForProposal } from "./resolveDefaultWeight.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { toCanonicalKg } from "./units.js";

const fmt = (n: number): string => String(Math.round(n * 100) / 100);

/** What a row in kilos needs to take a band (#336, brief §15 row 28): the
 *  athlete's MAX, and the unit the cells are written in. */
export type BandReference = { max: MaxForProposal | undefined; cellUnit: UnitsPreference };

/** A band and the number that drives it. */
export type RoundBand = { band: IntensityBand; value: string };

// One set's band. The axis decides the scale; kilos take one only from a MAX
// (kg / MAX = %); none and history place nothing on it.
const setBand = (set: ReadSet, ref: BandReference | undefined): RoundBand | null => {
  const v = set.value;
  if (v === null) return null;
  switch (set.kind) {
    case "percent-of-max":
      return { band: bandOf(v)!, value: fmt(v) };
    case "rpe":
      return { band: rpeBand(v), value: fmt(v) };
    case "rir":
      return { band: rirBand(v), value: fmt(v) };
    case "absolute": {
      const max = ref?.max?.valueKg;
      if (!ref || !max || max <= 0) return null;
      // The band is read from the % as shown: 84.6 reads "85", so it bands as 85.
      const pct = Math.round(pctOfMax(toCanonicalKg(v, ref.cellUnit), max));
      return { band: bandOf(pct)!, value: String(Math.round(pct)) };
    }
    default:
      return null;
  }
};

// How hard a set is on its own axis, to order two sets of the same band
// (85, 90 and 95 % all sit in "max"). RIR counts down; mixed axes compare by
// the number written, which only decides within a band.
const hardness = (set: ReadSet, shown: string): number => {
  const n = Number(shown);
  return set.kind === "rir" ? -n : n;
};

/** The hardest set of one exercise in round `w`, the first one on a tie,
 *  with its band. Null when it does not run there or no set has a band. */
export const slotRoundPeak = (
  slot: SlotDraft,
  w: number,
  ref?: BandReference,
): (RoundBand & { set: ReadSet }) | null => {
  let best: (RoundBand & { set: ReadSet }) | null = null;
  for (const set of readRound(slot, w, false) ?? []) {
    const b = setBand(set, ref);
    if (!b) continue;
    const harder =
      !best ||
      BAND_RANK[b.band] > BAND_RANK[best.band] ||
      (b.band === best.band && hardness(set, b.value) > hardness(best.set, best.value));
    if (harder) best = { ...b, set };
  }
  return best;
};

/** The band of one exercise in round `w`: its hardest set's (slotRoundPeak). */
export const slotRoundBand = (slot: SlotDraft, w: number, ref?: BandReference): RoundBand | null => {
  const peak = slotRoundPeak(slot, w, ref);
  return peak && { band: peak.band, value: peak.value };
};

// The program's grid (IntensityGrid): one row per session,
// cycleWeeks columns, hardest banded set colours the cell. Slots opted
// out of a week (inactiveWeeks[w]) contribute nothing there. `ref`: the
// MAXes by exercise and the cells' unit, so a row in kilos colours too.
export function buildIntensityGridFromDraft(
  draft: Draft,
  ref?: { maxes: ReadonlyMap<string, MaxForProposal> | undefined; cellUnit: UnitsPreference },
): IntensityGrid {
  const weeks = draft.cycleWeeks;
  const days = draft.sessions.map((session, sessionIdx) => {
    const cells: WeekCell[] = [];
    for (let w = 0; w < weeks; w += 1) {
      let cell: RoundBand | null = null;
      let sets = 0;
      for (const slot of session.slots) {
        sets += setCountAt(slot, w);
        const b = slotRoundBand(slot, w, ref && { max: ref.maxes?.get(slot.exerciseId), cellUnit: ref.cellUnit });
        if (b && (!cell || BAND_RANK[b.band] > BAND_RANK[cell.band])) cell = b;
      }
      cells.push({ band: cell?.band ?? null, value: cell?.value ?? null, sets });
    }
    const band = cells.reduce<IntensityBand | null>((acc, c) => higherBand(acc, c.band), null);
    return { sessionIdx, name: session.name, cells, band };
  });
  return { weeks, days };
}
